/**
 * SectionProperty — identidad, ubicación, idioma, vocabulario y marca.
 *
 * El campo estrella es "Cómo llamas a cada alojamiento": al cambiarlo, el
 * texto de ejemplo debajo cambia con él, que es la forma más corta de enseñar
 * que el producto se adapta al cliente en lugar de imponer su jerga.
 */
import React from "react";
import { Image as ImageIcon, Translate } from "@phosphor-icons/react";
import {
  Block, Card, FieldGrid, Field, TextArea, Select, Note,
} from "@/pages/admin/pms/settings/SettingsPrimitives";
import { useSettingsV2, updateSection, unitLabelFor } from "@/lib/settingsV2Store";
import {
  PROPERTY_TYPES, UNIT_LABELS, TIMEZONES, CURRENCIES, LANGUAGES,
} from "@/data/settingsV2Demo";

export default function SectionProperty() {
  const { property } = useSettingsV2();
  const set = (patch) => updateSection("property", patch);
  const label = unitLabelFor(property);

  return (
    <div data-testid="section-property">
      <Block
        title="Identidad"
        description="El nombre y la descripción que verán huéspedes y propietarios."
        testId="property-identity"
      >
        <Card>
          <FieldGrid>
            <Field
              label="Nombre de la propiedad"
              value={property.name}
              onChange={(v) => set({ name: v })}
              usedIn="property_name"
              testId="property-name"
            />
            <Select
              label="Tipo de propiedad"
              value={property.type}
              onChange={(v) => set({ type: v })}
              options={PROPERTY_TYPES}
              testId="property-type"
            />
            <Field
              label="Código interno"
              value={property.internal_code}
              onChange={(v) => set({ internal_code: v })}
              hint="Solo para uso interno"
              testId="property-code"
            />
            <div className="hidden md:block" aria-hidden="true" />
            <TextArea
              label="Descripción"
              value={property.description}
              onChange={(v) => set({ description: v })}
              rows={3}
              testId="property-description"
            />
          </FieldGrid>
        </Card>
      </Block>

      <Block
        title="Ubicación"
        description="Se usa en la web, en el portal del huésped y en las indicaciones de llegada."
        testId="property-location"
      >
        <Card>
          <FieldGrid>
            <Field label="País" value={property.country} onChange={(v) => set({ country: v })} testId="property-country" />
            <Field label="Región" value={property.region} onChange={(v) => set({ region: v })} testId="property-region" />
            <Field label="Ciudad o zona" value={property.city} onChange={(v) => set({ city: v })} testId="property-city" />
            <Select
              label="Zona horaria"
              value={property.timezone}
              onChange={(v) => set({ timezone: v })}
              options={TIMEZONES.map((t) => ({ value: t, label: t }))}
              hint="Define qué es «hoy» en operaciones"
              testId="property-timezone"
            />
            <Field
              label="Dirección"
              value={property.address}
              onChange={(v) => set({ address: v })}
              span={2}
              testId="property-address"
            />
          </FieldGrid>
        </Card>
      </Block>

      <Block
        title="Moneda e idiomas"
        description="La moneda operativa es con la que se paga en destino; la de informes es la de los extractos."
        testId="property-regional"
      >
        <Card>
          <FieldGrid>
            <Select
              label="Moneda operativa"
              value={property.currency_operational}
              onChange={(v) => set({ currency_operational: v })}
              options={CURRENCIES.map((c) => ({ value: c, label: c }))}
              testId="property-currency-operational"
            />
            <Select
              label="Moneda de informes"
              value={property.currency_reporting}
              onChange={(v) => set({ currency_reporting: v })}
              options={CURRENCIES.map((c) => ({ value: c, label: c }))}
              testId="property-currency-reporting"
            />
            <div className="md:col-span-2">
              <div className="flex items-center gap-2 mb-1.5">
                <span className="text-[11px] tracking-[0.18em] uppercase text-stone-500 dark:text-stone-400">
                  Idiomas
                </span>
                <Translate size={12} className="text-stone-400" />
              </div>
              <div className="flex flex-wrap gap-2">
                {LANGUAGES.map((l) => {
                  const on = (property.languages || []).includes(l.code);
                  return (
                    <button
                      key={l.code}
                      type="button"
                      onClick={() => {
                        const next = on
                          ? property.languages.filter((c) => c !== l.code)
                          : [...(property.languages || []), l.code];
                        if (next.length === 0) return; // siempre queda uno
                        // Si se quita el idioma que era el de por defecto, el
                        // desplegable se quedaría sin opción válida: se reasigna.
                        const patch = { languages: next };
                        if (!next.includes(property.default_guest_language)) {
                          patch.default_guest_language = next[0];
                        }
                        set(patch);
                      }}
                      data-testid={`property-language-${l.code}`}
                      aria-pressed={on}
                      className={`min-h-[44px] px-4 rounded-full text-sm border transition-colors ${
                        on
                          ? "bg-stone-900 text-white border-stone-900 dark:bg-stone-100 dark:text-stone-900 dark:border-stone-100"
                          : "bg-white text-stone-600 border-stone-300 dark:bg-stone-950 dark:text-stone-400 dark:border-stone-700"
                      }`}
                    >
                      {l.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <Select
              label="Idioma por defecto del huésped"
              value={property.default_guest_language}
              onChange={(v) => set({ default_guest_language: v })}
              options={LANGUAGES.filter((l) => (property.languages || []).includes(l.code))
                .map((l) => ({ value: l.code, label: l.label }))}
              testId="property-default-language"
            />
          </FieldGrid>
        </Card>
      </Block>

      <Block
        title="Vocabulario"
        description="El producto habla como hablas tú. Elige cómo llamas a cada alojamiento y el resto del sistema lo adopta."
        testId="property-terminology"
      >
        <Card>
          <FieldGrid>
            <Select
              label="Cómo llamas a cada alojamiento"
              value={property.unit_label}
              onChange={(v) => set({ unit_label: v })}
              options={UNIT_LABELS.map((l) => ({ value: l.value, label: l.singular }))}
              usedIn="unit_label"
              testId="property-unit-label"
            />
            {property.unit_label === "custom" && (
              <Field
                label="Término personalizado"
                value={property.unit_label_custom}
                onChange={(v) => set({ unit_label_custom: v })}
                placeholder="Suite, Cabaña, Lodge…"
                testId="property-unit-label-custom"
              />
            )}
          </FieldGrid>

          <div
            className="mt-5 pt-4 border-t border-stone-100 dark:border-stone-800 text-sm text-stone-600 dark:text-stone-300"
            data-testid="property-unit-label-preview"
          >
            Con esta elección, el sistema dirá{" "}
            <span className="text-stone-900 dark:text-stone-100">
              «{label.plural} disponibles»
            </span>
            ,{" "}
            <span className="text-stone-900 dark:text-stone-100">
              «Estado de la {label.singular.toLowerCase()}»
            </span>{" "}
            y{" "}
            <span className="text-stone-900 dark:text-stone-100">
              «Propietario de la {label.singular.toLowerCase()}»
            </span>{" "}
            en el PMS, el portal del huésped, la web y los informes.
          </div>
        </Card>
      </Block>

      <Block
        title="Operativa y contacto"
        description="Horarios y canales de contacto que aparecen en la confirmación y en el portal."
        testId="property-operational"
      >
        <Card>
          <FieldGrid>
            <Field
              label="Hora de entrada"
              type="time"
              value={property.check_in_time}
              onChange={(v) => set({ check_in_time: v })}
              usedIn="check_times"
              testId="property-checkin"
            />
            <Field
              label="Hora de salida"
              type="time"
              value={property.check_out_time}
              onChange={(v) => set({ check_out_time: v })}
              testId="property-checkout"
            />
            <Field label="Teléfono" value={property.contact_phone} onChange={(v) => set({ contact_phone: v })} testId="property-phone" />
            <Field label="WhatsApp" value={property.contact_whatsapp} onChange={(v) => set({ contact_whatsapp: v })} testId="property-whatsapp" />
            <Field label="Email" type="email" value={property.contact_email} onChange={(v) => set({ contact_email: v })} span={2} testId="property-email" />
          </FieldGrid>
        </Card>
      </Block>

      <Block
        title="Marca"
        description="El logotipo y la imagen principal alimentarán la web, el portal y los documentos."
        testId="property-brand"
      >
        <Note>En la vista previa las imágenes son marcadores de posición: no se sube ningún archivo.</Note>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <MediaSlot
            label="Logotipo"
            filled={property.logo_set}
            onToggle={() => set({ logo_set: !property.logo_set })}
            testId="property-logo"
          />
          <MediaSlot
            label="Imagen principal"
            filled={property.cover_set}
            onToggle={() => set({ cover_set: !property.cover_set })}
            testId="property-cover"
            tall
          />
        </div>
      </Block>
    </div>
  );
}

function MediaSlot({ label, filled, onToggle, testId, tall }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      data-testid={testId}
      aria-pressed={filled}
      className={`w-full rounded-xl border border-dashed flex flex-col items-center justify-center gap-2 transition-colors ${
        tall ? "min-h-[132px]" : "min-h-[108px]"
      } ${
        filled
          ? "border-stone-300 bg-stone-50 dark:border-stone-700 dark:bg-stone-900"
          : "border-stone-300 dark:border-stone-700 hover:border-stone-400"
      }`}
    >
      <ImageIcon size={20} className="text-stone-400" weight={filled ? "fill" : "regular"} />
      <span className="text-sm text-stone-700 dark:text-stone-300">{label}</span>
      <span className="text-[11px] text-stone-400">
        {filled ? "Configurado · pulsa para vaciar" : "Sin configurar · pulsa para simular"}
      </span>
    </button>
  );
}
